import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RcFile } from 'antd/lib/upload/interface';
import * as XLSX from 'xlsx';

import { Button, Modal, Title } from '@gerdau/hefesto';
import { UploadFile } from '@gerdau/hefesto-mais-web';

import {
  Container,
  ContentButton,
  Header,
  Message,
  MessageWrapperChooseItem,
  Wrapper
} from './styles';
import SpreadsheetConfigModal from './SpreadsheetConfigModal';

import { ApplicationState } from '../../../../../store';
import {
  addSearchedItem as addSearchItemAction,
  cleanSpreadSheetMVP,
  removeSearchedItem as removeSearchedItemAction
} from '../../../../../store/ducks/long-steel-order-item-search/actions';
import {
  ECodeType,
  ESpreadsheetField,
  EUserPreferenceType,
  IOrderEntryPreference,
  ISaveUser
} from '../../../../../store/ducks/user/types';

import { ICustomer } from '../../../../../store/ducks/customers/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import MessageChooseItem from '../../../../../components/MessageChooseItem';
import { EOrderSettingsRoutes } from '../orderSettings.routes';
import { ILongSteelOrderItem } from '../../../../../store/ducks/long-steel-shopping-cart/types';
import LoadingItems from '../LoadingItems';
import { saveUser } from '../../../../../store/ducks/user/actions';
import { ShoppingCartBottomBar } from '../../../../../components/Purchase/BottomBar';
import { ILongSteelOrderItemSearchState } from '../../../../../store/ducks/long-steel-order-item-search/types';
import {
  BulkCartResponse,
  CartBulkEntry,
  NewCartItem
} from '../../../../../services/MVP/models';
import { useBulkErrorToast } from '../../../../../services/MVP/useBulkErrorToast';
import {
  setTipoCompraForSku,
  setLastTipoCompra
} from '../../../../../services/MVP/tipoCompraMap';
import { useCartService } from '../../../../../services/MVP/useCartService';
import {
  Hit,
  MultProductsResponse,
  useProductForCustomer
} from '../../../../../services/MVP/useProductForCustomer';
import { IProductCardTagging } from '../../../../../store/ducks/tagging/types';
import { cleanShoppingCart } from '../../../../../store/ducks/long-steel-shopping-cart/actions';
import { BlockedModal } from '../../../../../components/Purchase/BlockedModal';
import useOnRouteChange from '../../../../../hooks/useOnRouteChange';
import moment from 'moment/moment';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { ProductNotFoundChip } from '../SpreadsheetTableMvp/styles';
import { SelfSearchedListMvp } from '../SearchedListMvp/SelfSearchedListMvp';
import { magentoURLBuilder } from '../../../../../utils/magentoUrlBuilder';
import { commerceApi } from '../../../../../services/api';
import { EndpointsMvp } from '../../../../../services/MVP/enum';
import { useAuth } from '../../../../../services/MVP/useAuth';
import { GetFlag } from '../../../../../utils/featureFlag';
import { resolveValidMeasureUnit } from '../../../../../utils/measureUnit';

interface columnField {
  key: number;
  field: string;
  label: string;
  position: string;
}

interface formattedSheetEntryProps {
  SKU: number | string;
  CLIENT_SKU?: number | string;
  SKU_QUANTITY?: number;
  SKU_MEASURE_UNITY?: string;
  DESIRED_DATE?: string;
  CUSTOMER_PURCHASE_ITEM?: string;
  DESCRIPTION?: string;
}

interface formattedSheetOutProps {
  itemWeb: string;
  incoterms: string;
  sku: string;
  customerSku: string;
  customerPurchaseItem: string;
  quantity: number;
  measureUnit: string;
  desiredDate: string;
  description: string;
  destinations: any[];
  found: boolean;
  addedToCart: boolean;
  itemSaleInfo: {
    material: string;
    requiredDate: string;
    quantity: string;
    unityMeasure: string;
    customerPurchaseItem: string;
  };
}

function measureUnityValidator({ unit = '-' }: { unit?: string }): string {
  const KG: string[] = [
    'kg',
    'kilo',
    'kilos',
    'quilo',
    'quilos',
    'quilograma',
    'quilogramas',
    'k.',
    'k',
    'kgs',
    'kgs.',
    'kg.',
    'kgm',
    'kgm.',
    'kgms',
    'kgms.'
  ];
  const T: string[] = [
    'to',
    'to.',
    't',
    'tonelada',
    'ton.',
    'ton',
    'tons',
    'tons.',
    'toneladas',
    't.',
    'tn',
    'tn.',
    'tns',
    'tns.'
  ];
  const CX: string[] = ['cx', 'caixa', 'caixas', 'cxs', 'cx.', 'cxs.'];
  const RL: string[] = ['rl', 'rl.', 'rls', 'rls.', 'rolo', 'rolos'];
  const PC: string[] = [
    'pc',
    'peça',
    'peças',
    'peca',
    'pecas',
    'pcs',
    'pc.',
    'pcs.'
  ];
  const BR: string[] = ['br'];
  const FX: string[] = ['fx'];

  const normalizedUnit = unit.toLowerCase();

  if (KG.includes(normalizedUnit)) return 'KG';
  if (T.includes(normalizedUnit)) return 'T';
  if (CX.includes(normalizedUnit)) return 'CX';
  if (RL.includes(normalizedUnit)) return 'RL';
  if (PC.includes(normalizedUnit)) return 'PC';
  if (BR.includes(normalizedUnit)) return 'BR';
  if (FX.includes(normalizedUnit)) return 'FX';

  return normalizedUnit?.toLocaleUpperCase();
}

function convertToSheetResponse({
  formattedSheetEntry,
  internalCode
}: {
  formattedSheetEntry: formattedSheetEntryProps;
  internalCode: boolean;
}): formattedSheetOutProps {
  return {
    customerPurchaseItem:
      formattedSheetEntry.CUSTOMER_PURCHASE_ITEM?.toString() ?? '',
    itemWeb: '',
    incoterms: '',
    sku:
      (internalCode
        ? formattedSheetEntry.CLIENT_SKU?.toString()
        : formattedSheetEntry.SKU?.toString()) ?? '',
    customerSku: formattedSheetEntry.CLIENT_SKU?.toString() ?? '',
    quantity: formattedSheetEntry.SKU_QUANTITY ?? 0,
    measureUnit: measureUnityValidator({
      unit: formattedSheetEntry.SKU_MEASURE_UNITY?.trim()
    }),
    desiredDate: formattedSheetEntry.DESIRED_DATE ?? '',
    description: formattedSheetEntry.DESCRIPTION ?? '',
    destinations: [
      {
        description: '',
        condGroup: '',
        company: '',
        salesOrg: '',
        distributionChannel: '',
        division: '',
        salesOffice: '',
        salesGroup: '',
        plant: '',
        plantDescription: '',
        multiPlant: false,
        minimumDate: '',
        measureUnits: [
          {
            measure: '',
            minimumQuantity: '',
            multiple: '',
            convMeasureKg: '',
            convMeasureTo: ''
          }
        ],
        paymentTerms: [
          {
            term: '',
            description: ''
          }
        ],
        fiscalBenefits: []
      }
    ],
    found: true,
    addedToCart: true,
    itemSaleInfo: {
      material: formattedSheetEntry.SKU?.toString() ?? '',
      requiredDate: formattedSheetEntry.DESIRED_DATE ?? '',
      quantity: (formattedSheetEntry.SKU_QUANTITY ?? 0).toFixed(3),
      unityMeasure: formattedSheetEntry.SKU_MEASURE_UNITY ?? '',
      customerPurchaseItem: formattedSheetEntry.CUSTOMER_PURCHASE_ITEM ?? ''
    }
  };
}

const orderEntryPreferenceDefault: IOrderEntryPreference = {
  name: EUserPreferenceType.ORDER_ENTRY,
  codeType: ECodeType.SKU,
  spreadsheetLine: 3,
  spreadsheetColumns: [
    {
      key: 0,
      field: ESpreadsheetField.SKU,
      label: 'Código da Gerdau',
      position: 'A'
    },
    {
      key: 1,
      field: ESpreadsheetField.CLIENT_SKU,
      label: 'Meu código interno',
      position: ''
    },
    {
      key: 2,
      field: ESpreadsheetField.QUANTITY,
      label: 'Quantidade',
      position: 'B'
    },
    { key: 3, field: ESpreadsheetField.UNITY, label: 'Unidade', position: 'C' },
    {
      key: 4,
      field: ESpreadsheetField.DESIRED_DATE,
      label: 'Data desejada',
      position: 'D'
    },
    {
      key: 5,
      field: ESpreadsheetField.CUSTOMER_PURCHASE_ITEM,
      label: 'Item do pedido',
      position: 'E'
    }
  ]
};

interface ISpreadsheetUploadMvpProps {
  emitterId: string;
  receiverId: string;
}

export const SelfSpreadsheetUploadMvp: React.FC<ISpreadsheetUploadMvpProps> = ({
  emitterId,
  receiverId
}) => {
  const cart = useCartService();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { dispatchBulkErrorToast } = useBulkErrorToast();
  const { getMultProducts, getProductForCustomerInternalCode } =
    useProductForCustomer();
  const { getHeaders } = useAuth();
  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');
  const [filename, setFilename] = useState<string>('');
  const [internalCodeSkus, setInternalCodeSkus] = useState<{
    [key: string]: string;
  }>({});

  const { commerceScreenName } = getTaggingInformationScreen(
    document.location.pathname
  );

  const [spreadSheetData, setSpreadSheetData] = useState<ILongSteelOrderItem[]>(
    []
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [magentoProducts, setMagentoProducts] = useState<
    MultProductsResponse[] | null
  >(null);
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(true);

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const orderItemSearch = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch
  );
  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );

  const user = useSelector((state: ApplicationState) => state.user);
  const userLoading = useSelector(
    (state: ApplicationState) => state.user.loading
  );
  const userPreferences = useSelector(
    (state: ApplicationState) => state.user.preferences
  );

  const selectedCustomer = customersState.customers.find(
    (c) => c.customerId === emitterId
  );
  const isBlockedCustomer =
    selectedCustomer?.blocks &&
    selectedCustomer?.blocks?.filter((blc) => blc.type === 'order')?.length > 0;
  const [openBlockedModal, setOpenBlockedModal] = useState(isBlockedCustomer);
  const onCloseBlockedModal = () => {
    dispatch(cleanShoppingCart());
    setOpenBlockedModal(false);
  };

  const isMounted = useRef(true);

  const cleanMagentoProducts = () => setMagentoProducts(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const createSpreadsheetSubmissionTags = (
    type:
      | 'baixar_modelo_planilha'
      | 'configurar_planilha'
      | 'enviar_planilha'
      | 'upload_planilha'
  ) => {
    dispatch(
      sendTagging({
        screenName: 'enviar_planilha',
        etapa: 'enviar_planilha',
        event: 'envio_planilha',
        tipo_envio: `${type}`
      })
    );
  };

  useEffect(() => {
    setOpenBlockedModal(isBlockedCustomer);
  }, [selectedCustomer]);

  useOnRouteChange(() => {
    dispatch(cleanSpreadSheetMVP());
  });

  const handleTagging = (buttonLabel: string) => {
    dispatch(
      sendTagging({
        event: 'click',
        screenName: commerceScreenName,
        etapa: 'selecionar_emissor_recebedor',
        botao_navegacao: buttonLabel
      })
    );
  };

  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById ||
    !!userLoading;

  const isLoading = customerLoading || loading;

  const [canShowConfigModal, setCanShowConfigModal] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProductsNotFoundModal, setShowProductsNotFoundModal] =
    useState(false);

  const getUserOrderEntryPreference = (): IOrderEntryPreference => {
    const orderEntryIdx = userPreferences.findIndex(
      (preference) => preference.name === EUserPreferenceType.ORDER_ENTRY
    );

    useEffect(() => {
      canShowConfigModal && !isLoading
        ? createSpreadsheetSubmissionTags('configurar_planilha')
        : '';
    }, [canShowConfigModal]);
    if (
      orderEntryIdx !== -1 &&
      (userPreferences[orderEntryIdx] as IOrderEntryPreference)
        .spreadsheetColumns
    ) {
      const orderEntryPreferences = userPreferences[
        orderEntryIdx
      ] as IOrderEntryPreference;

      if (
        orderEntryPreferences.spreadsheetColumns.length <
        orderEntryPreferenceDefault.spreadsheetColumns.length
      ) {
        orderEntryPreferenceDefault.spreadsheetColumns.forEach((col) => {
          if (
            !orderEntryPreferences.spreadsheetColumns.find(
              (item) => item.field === col.field
            )
          ) {
            orderEntryPreferences.spreadsheetColumns.push({
              ...col,
              position: ''
            });
          }
        });
      }

      return orderEntryPreferences;
    } else {
      return orderEntryPreferenceDefault;
    }
  };

  const [userOrderEntryPreference, setUserOrderEntryPreference] =
    useState<IOrderEntryPreference>(getUserOrderEntryPreference());

  const updateUserOrderEntryPreference = (
    newUserOrderEntryPreference: IOrderEntryPreference
  ) => {
    const saveUserProps: ISaveUser = {
      user: user,
      userPreference: newUserOrderEntryPreference
    };
    dispatch(saveUser(saveUserProps));
    // dispatch(updateUserPreferences(newUserOrderEntryPreference));
    setUserOrderEntryPreference(newUserOrderEntryPreference);
    setCanShowConfigModal(false);
  };

  const handleRemoveSearchedItems = () => {
    dispatch(removeSearchedItemAction([]));
    setSpreadSheetData([]);
    setShowDeleteModal(false);
    const filteredPrices = filterPricesAndsearchedItems();
    dispatch(
      sendTagging({
        event: 'remove_from_cart',
        ecommerce: {
          screenName: 'sua_planilha',
          tipo_compra: 'planilha',
          paginacao: '1',
          items: spreadSheetData.map(
            (item) =>
              ({
                screenName: 'sua_planilha',
                tipo_compra: 'planilha',
                item_id: item.sku ?? '',
                item_name: item.description,
                price: filteredPrices.find(
                  (filteredProduct) => filteredProduct.sku === item.sku
                )?.price,
                unidade_de_medida: item.measureUnit,
                quantity: item.quantity?.toString() ?? 1,
                item_category: filteredPrices.find(
                  (filteredProduct) => filteredProduct.sku === item.sku
                )?.description,
                cod_gerdau: item.sku,
                preco_acordado: filteredPrices.find(
                  (filteredProduct) => filteredProduct.sku === item.sku
                )?.hasAgreementPrice
              } as IProductCardTagging)
          )
        }
      })
    );
    cleanMagentoProducts();
  };

  const handleCancelModal = () => {
    setShowDeleteModal(false);
  };
  const handleTaggingProductNotFoundModal = (buttonLabel: string) => {
    dispatch(
      sendTagging({
        event: 'modal_produtos_nao_identificados',
        screenName: 'planilha',
        botao_navegacao: buttonLabel
      })
    );
  };

  const removeAllSearchedItem = () => {
    setShowDeleteModal(true);
  };

  const ModalRemoveAllSearchedItems = () => {
    if (!showDeleteModal) {
      return null;
    }

    return (
      <Modal
        buttons={{
          left: {
            children: 'Manter todos',
            name: 'cancel',
            onClick: handleCancelModal,
            type: 'button',
            variant: 'secondary'
          },
          right: {
            children: 'Excluir todos',
            name: 'delete-all',
            onClick: handleRemoveSearchedItems,
            type: 'button',
            variant: 'danger'
          }
        }}
        modalSize="sm"
        title="Excluir todos"
      >
        Tem certeza que deseja excluir todos os itens?
      </Modal>
    );
  };

  const ModalProductNotFound = () => {
    if (!showProductsNotFoundModal) {
      return null;
    }

    const closeModal = () => {
      setShowProductsNotFoundModal(false);
      handleTaggingProductNotFoundModal('Voltar');
    };
    dispatch(
      sendTagging({
        event: 'informacoes_nao_identificadas',
        screenName: 'planilha',
        mensagem: 'Deseja avançar para o carrinho mesmo assim?'
      })
    );
    return (
      <Modal
        buttons={{
          left: {
            children: 'Voltar',
            name: 'back',
            onClick: closeModal,
            type: 'button',
            variant: 'secondary'
          },
          right: {
            children: 'Avançar',
            name: 'next',
            onClick: onContinueToCheckout,
            type: 'button',
            variant: 'primary'
          }
        }}
        modalSize="sm"
        title="Sua planilha contém inconsistências nos dados"
      >
        Deseja avançar para o carrinho mesmo assim?
      </Modal>
    );
  };

  const addSearchedItem = (items: ILongSteelOrderItem[]) => {
    const updatedSearchedItems = spreadSheetData.map(
      (searchItem: ILongSteelOrderItem, index: number) => {
        const findSelectedItem = items.find(
          (addItem: ILongSteelOrderItem) => addItem.tableId === index
        );
        return findSelectedItem
          ? { ...searchItem, addedToCart: true }
          : searchItem;
      }
    );
    dispatch(addSearchItemAction([...updatedSearchedItems]));
  };

  const removeSearchedItem = (productIndex: number) => {
    const searchedItems = spreadSheetData;
    dispatch(
      removeSearchedItemAction([
        ...searchedItems.filter((item, index) => index !== productIndex)
      ])
    );
    const removedItem = searchedItems.find(
      (item, index) => index === productIndex
    );

    setSpreadSheetData((prev) =>
      prev.filter((_, index) => index !== productIndex)
    );

    if (removedItem) {
      const matchingPrice =
        magentoProducts &&
        magentoProducts.find((product) => product.sku === removedItem.sku);
      const price = matchingPrice
        ? matchingPrice.price.isAgreementPrice === 'X'
          ? String(matchingPrice.price.salesAgreementData.salesAgreement)
          : matchingPrice.price.creditPrice
        : undefined;
      const filteredProducts = filterPricesAndsearchedItems();

      dispatch(
        sendTagging({
          event: 'remove_from_cart',
          ecommerce: {
            screenName: 'sua_planilha',
            tipo_compra: 'planilha',
            paginacao: '',
            items: [
              {
                screenName: 'sua_planilha',
                tipo_compra: 'planilha',
                item_id: removedItem.sku ?? '',
                item_name: removedItem.description,
                price: price,
                unidade_de_medida: removedItem.measureUnit,
                quantity: removedItem.quantity?.toString() ?? 1,
                item_category: filteredProducts.find(
                  (filteredProduct) => filteredProduct.sku === removedItem.sku
                )?.description,
                cod_gerdau: removedItem.sku,
                preco_acordado: filteredProducts.find(
                  (filteredProduct) => filteredProduct.sku === removedItem.sku
                )?.hasAgreementPrice
              }
            ]
          }
        })
      );
    }
  };

  const onConfirmSpreadsheetConfig = (
    orderEntryPreference: IOrderEntryPreference,
    hasConfigChanged: boolean
  ) => {
    if (hasConfigChanged) {
      updateUserOrderEntryPreference(orderEntryPreference);
      return;
    }
    setCanShowConfigModal(false);
  };

  const customerData: ICustomer = customersState.customers.find(
    (customer) => customer.customerId === emitterId
  ) as ICustomer;

  const readSheet = (
    file: File,
    field: columnField[],
    startRow = 2
  ): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const data = new Uint8Array(e.target!.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];

          if (!sheet['!ref']) return resolve([]);

          const range = XLSX.utils.decode_range(sheet['!ref']!);
          const fieldsIndex = field
            .filter((c) => c.position)
            .map((field) => ({
              ...field,
              colIdx: XLSX.utils.decode_col(field.position.toUpperCase())
            }));

          const result: any[] = [];
          const initialRow = startRow - 1;

          for (let row = initialRow; row <= range.e.r; row++) {
            const atRow: Record<string, any> = {};
            let emptyRow = true;

            fieldsIndex.forEach((fieldIdx) => {
              const cellAddress = XLSX.utils.encode_cell({
                r: row,
                c: fieldIdx.colIdx
              });
              const cell = sheet[cellAddress];
              let value = cell ? cell.v : null;

              if (value !== null && value !== '') emptyRow = false;

              if (
                fieldIdx.field.includes('QUANTITY') ||
                fieldIdx.field === 'QTD'
              ) {
                if (value === null || value === undefined || value === '') {
                  value = 1;
                } else if (typeof value === 'string') {
                  const sanitized = value.trim().replace(',', '.');
                  const parsed = parseFloat(sanitized);
                  value = isNaN(parsed) ? 1 : parsed;
                } else if (typeof value === 'number') {
                  value = isNaN(value) ? 1 : value;
                }
              } else if (fieldIdx.field === 'DESIRED_DATE' && value) {
                if (typeof value === 'number') {
                  const dataJS = XLSX.SSF.parse_date_code(value);
                  value = dataJS
                    ? moment(new Date(dataJS.y, dataJS.m - 1, dataJS.d)).format(
                        'YYYY-MM-DD'
                      )
                    : null;
                } else if (typeof value === 'string') {
                  const dataMoment = moment(
                    value,
                    ['DD/MM/YYYY', 'DD.MM.YYYY', 'YYYY-MM-DD'],
                    true
                  );
                  value = dataMoment.isValid()
                    ? dataMoment.format('YYYY-MM-DD')
                    : null;
                }
              }

              atRow[fieldIdx.field] = value;
            });

            if (!emptyRow) result.push(atRow);
          }

          resolve(result);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const onFileUploaded = async (file: RcFile) => {
    try {
      if (file) {
        cleanMagentoProducts();
        const result = await readSheet(
          file,
          userOrderEntryPreference.spreadsheetColumns,
          userOrderEntryPreference.spreadsheetLine
        );
        setSpreadSheetData(
          result.map((el) =>
            convertToSheetResponse({
              formattedSheetEntry: el,
              internalCode: userOrderEntryPreference?.codeType === 'CLIENT_SKU'
            })
          ) as ILongSteelOrderItem[]
        );
      }

      setFilename(file.name);
      dispatch(
        sendTagging({
          etapa: 'planilha_carregada',
          screenName: 'enviar_planilha',
          event: 'planilha_carregada'
        })
      );
    } catch (e) {
      console.error(e);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Erro ao tentar ler planilha de produtos',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    }
  };

  const searchedItems = useSelector(
    (state: { longSteelOrderItemSearch: ILongSteelOrderItemSearchState }) =>
      state.longSteelOrderItemSearch.searchedItems
  );

  const { push } = useHistory();

  const convertSpreadsheetResponse = async (
    orderItems: ILongSteelOrderItem[]
  ): Promise<CartBulkEntry[] | undefined> => {
    if (magentoProducts) {
      return orderItems
        .filter((orderItem) => {
          const hasInternalCode =
            userOrderEntryPreference &&
            userOrderEntryPreference?.codeType === 'CLIENT_SKU' &&
            orderItem.customerSku.length > 0;

          const itemSku = hasInternalCode
            ? magentoProducts.find(
                (el) =>
                  el.extension_attributes?.internal_code ===
                  orderItem.customerSku
              )?.sku
            : orderItem.sku;

          const magProduct = magentoProducts.find(
            (product) => product.sku === itemSku
          );
          return (
            magProduct &&
            // confirmando que o preço existe e tem preço
            magProduct.price &&
            magProduct.price.creditPrice &&
            ((!isNaN(Number(magProduct.price.creditPrice)) &&
              Number(magProduct.price.creditPrice) > 0) ||
              (magProduct.price.isAgreementPrice === 'X' &&
                !isNaN(
                  Number(magProduct.price.salesAgreementData.salesAgreement)
                ) &&
                Number(magProduct.price.salesAgreementData.salesAgreement) > 0))
          );
        })
        .map((orderItem) => {
          const hasInternalCode =
            userOrderEntryPreference &&
            userOrderEntryPreference?.codeType === 'CLIENT_SKU' &&
            orderItem.customerSku.length > 0;

          const itemSku = hasInternalCode
            ? magentoProducts.find(
                (el) =>
                  el.extension_attributes?.internal_code ===
                  orderItem.customerSku
              )?.sku || orderItem.sku
            : orderItem.sku;

          const magProduct = magentoProducts.find(
            (product) => product.sku === itemSku
          );

          const entry: CartBulkEntry = {
            sku: itemSku,
            qty:
              !orderItem.quantity || isNaN(orderItem.quantity)
                ? 1
                : orderItem.quantity,
            quote_id: cart.getCartId(),
            extension_attributes: {
              measure_unity_gerdau: '',
              destination_gerdau: '',
              payment_type_gerdau: '',
              sap_fields: {
                o_header_sap: [],
                o_item_sap: [],
                o_price_sap: []
              }
            }
          };

          const isPriceAgreement = magProduct?.price.isAgreementPrice === 'X';
          entry.extension_attributes.has_agreed_price_gerdau = isPriceAgreement;

          let mu = orderItem.measureUnit; //magProduct?.price.um;

          const productOptions = magProduct?.productOptions;

          const currentDestination = magProduct?.price?.destination;

          const hasMeasureUnitOptions =
            !!productOptions &&
            !!currentDestination &&
            !!productOptions[currentDestination] &&
            !!productOptions[currentDestination].measureUnits &&
            productOptions[currentDestination].measureUnits.length > 0;

          if (hasMeasureUnitOptions && productOptions && currentDestination) {
            const availableUnits = productOptions[
              currentDestination
            ].measureUnits.map((m) => m.measure);
            mu = resolveValidMeasureUnit(
              orderItem.measureUnit,
              availableUnits,
              magProduct?.price.um ?? ''
            );
          }

          entry.extension_attributes.measure_unity_gerdau = mu ?? '';

          // expected_date_gerdau
          let minimumDate = '';
          if (orderItem.desiredDate.length) {
            minimumDate = new Date(orderItem.desiredDate)
              .toISOString()
              .replace('Z', '');
          }

          /**
           * ADCL-6410
           * Alteração na lógica para considerar a data minima
           */

          const afterHalfDay = moment()
            .utcOffset(-3)
            .isAfter(
              moment().utcOffset(-3).set({ hour: 12, minute: 0, second: 0 })
            );

          const minDate = moment()
            .utcOffset(-3)
            .add(afterHalfDay ? 5 : 4, 'day');

          const noBusinessMinDate = [0, 6].includes(minDate.day());

          const businessMinDate = noBusinessMinDate
            ? minDate.add(minDate.day() === 0 ? 1 : 2, 'day')
            : minDate;

          const today = moment().startOf('day');
          const date = moment(minimumDate);

          const noBusinessMinimumDate = [0, 6].includes(
            moment(minimumDate).day()
          );

          const businessMinimumDate = noBusinessMinimumDate
            ? moment(minimumDate).add(
                moment(minimumDate).day() === 0 ? 1 : 2,
                'day'
              )
            : moment(minimumDate);

          if (
            !minimumDate ||
            minimumDate.length === 0 ||
            (minimumDate && date.isSameOrBefore(today))
          ) {
            entry.extension_attributes.expected_date_gerdau = '';
          } else {
            entry.extension_attributes.expected_date_gerdau =
              minimumDate &&
              businessMinimumDate &&
              businessMinimumDate.isValid() &&
              businessMinimumDate.isAfter(businessMinDate)
                ? businessMinimumDate.format('YYYY-MM-DD')
                : businessMinDate.format('YYYY-MM-DD');
          }

          // destination
          entry.extension_attributes.destination_gerdau = isPriceAgreement
            ? magProduct?.price.salesAgreementData.destination ?? ''
            : magProduct?.price.destination ?? '';

          // payment_type_gerdau
          entry.extension_attributes.payment_type_gerdau = isPriceAgreement
            ? magProduct?.price.salesAgreementData.paymentTerms ?? 'Z021'
            : magProduct?.price.paymentTerms ?? 'Z021';

          return entry;
        });
    }
    return;
  };

  const onContinueToCheckout = async () => {
    // handleTaggingProductNotFoundModal('Avançar');
    try {
      setShowProductsNotFoundModal(false);
      setLoading(true);
      if (isMounted.current) {
        setIsContinueDisabled(true);
      }

      const filterOnlyFoundProducts = spreadSheetData.filter(
        (item) => item.found
      );
      const entries = await convertSpreadsheetResponse(filterOnlyFoundProducts);
      if (!entries) {
        throw new Error('Erro ao tentar adicionar os itens no carrinho!');
      }
      const qtyToAddOnCart = 30 - Number(cartQuantity);
      if (qtyToAddOnCart > 0) {
        const bulkCartAdd = (await cart.addBulkCartItem({
          items: entries.slice(0, qtyToAddOnCart) as NewCartItem[],
          withOptions: false,
          withPrice: false,
          noCheckPrice: true
        })) as BulkCartResponse;

        const bulkItems = bulkCartAdd?.items ?? [];

        dispatchBulkErrorToast(bulkItems, bulkCartAdd?.errors ?? []);

        if (bulkItems.length > 0) {
          const hasMeasureUnityChange = bulkItems.filter(
            (item) => !!item?.extension_attributes?.has_unit_measure_conversion
          );

          createContinueToCheckoutTag();
          push(`./${EOrderSettingsRoutes.SHOPPING_CART}`);

          if (hasMeasureUnityChange && hasMeasureUnityChange.length > 0) {
            for (let index = 0; index < hasMeasureUnityChange.length; index++) {
              dispatch(
                addToast({
                  type: EToastTypes.WARNING,
                  message: `Unidade de medida do produto ${
                    hasMeasureUnityChange[index].sku || ''
                  } não identificada. A unidade padrão ${
                    hasMeasureUnityChange[index]?.extension_attributes
                      ?.measure_unity_gerdau || ''
                  } foi aplicada.`,
                  durationInMS: 5000,
                  labelActionOne: 'Fechar'
                })
              );
            }
          }
        } else {
          throw new Error('Erro ao tentar adicionar os itens no carrinho!');
        }
      } else {
        dispatch(
          sendTagging({
            event: 'error',
            screenName: commerceScreenName,
            mensagem: 'Seu carrinho atingiu o limite de 30 produtos.'
          })
        );
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Seu carrinho atingiu o limite de 30 produtos.',
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Erro ao tentar adicionar os itens no carrinho!'
        })
      );
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setIsContinueDisabled(false);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toType = (obj: any): string => {
    return (
      {}.toString
        .call(obj)
        .match(/\s([a-zA-Z]+)/)?.[1]
        .toLowerCase() || ''
    );
  };

  const itemUnavailable = (
    <ProductNotFoundChip>Indisponível</ProductNotFoundChip>
  );

  const measureUnitFormat = (measureUnit: string | ReactNode) => {
    const regex = /[a-zA-Z]/;
    if (typeof measureUnit === 'string' && regex.test(measureUnit)) {
      return measureUnit;
    }
    return itemUnavailable;
  };

  const dateFormat = (date: string | Date | ReactNode) => {
    const dateType = toType(date);
    if (['string', 'date'].includes(dateType)) {
      const today = moment().startOf('day');
      const value = moment(
        dateType === 'date' ? (date as Date) : (date as string)
      );
      if (value.isSameOrBefore(today)) {
        return itemUnavailable;
      }
      if (value.isValid()) {
        return value.format('DD/MM/YYYY');
      }
      return '-';
    }
    return date;
  };

  const hasInvalidMeasureUnitOrDate = (
    items: ILongSteelOrderItem[]
  ): boolean => {
    return items.some(
      (item) => item.measureUnit === '' || item.desiredDate === ''
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasUnavailableDateOrMeasureUnit = (items: any[]): boolean => {
    return items.some((item) => {
      const formattedDate = dateFormat(item.desiredDate);
      const formattedMeasureUnit = measureUnitFormat(item.measureUnit);
      return (
        (React.isValidElement(formattedDate) &&
          formattedDate.type === itemUnavailable.type) ||
        (React.isValidElement(formattedMeasureUnit) &&
          formattedMeasureUnit.type === itemUnavailable.type)
      );
    });
  };

  const goToNextStep = async (productsNotFound: boolean) => {
    handleTagging('avancar_para_carrinho');
    const hasInvalidData = hasInvalidMeasureUnitOrDate(spreadSheetData);
    const hasUnavailableData = hasUnavailableDateOrMeasureUnit(spreadSheetData);
    setTimeout(
      () =>
        setShowProductsNotFoundModal(
          productsNotFound || hasInvalidData || hasUnavailableData
        ),
      0
    );
    if (!productsNotFound && !hasInvalidData && !hasUnavailableData) {
      await onContinueToCheckout();
    }
  };

  const createContinueToCheckoutTag = () => {
    const filteredPrices = spreadSheetData.map((item) => {
      const findMatchingPrice = magentoProducts?.find(
        (priceItem) => priceItem.sku === item.sku
      );

      if (!findMatchingPrice) {
        return {
          ...item,
          price: ''
        };
      }

      const matchingPrice = findMatchingPrice as unknown as NewCartItem;

      if (!matchingPrice.price) {
        return {
          ...item,
          price: ''
        };
      }

      const price =
        matchingPrice.price.isAgreementPrice === 'X' &&
        matchingPrice.price.salesAgreementData.salesAgreement
          ? String(matchingPrice.price.salesAgreementData.salesAgreement)
          : matchingPrice.price.creditPrice;

      return {
        ...item,
        price
      };
    });
    const filteredProducts = filterPricesAndsearchedItems();
    dispatch(
      sendTagging({
        event: 'add_to_cart',
        ecommerce: {
          screenName: 'sua_planilha',
          tipo_compra: 'planilha',
          paginacao: '',
          items: filteredPrices.map((item) => ({
            screenName: 'sua_planilha',
            tipo_compra: 'planilha',
            item_id: item?.sku || '',
            item_name: item.description,
            price: item.price,
            unidade_de_medida: item.measureUnit,
            quantity: item.quantity?.toString() ?? 1,
            item_category: filteredProducts.find(
              (filteredProduct) => filteredProduct.sku === item.sku
            )?.description,
            cod_gerdau: item.sku,
            preco_acordado: true
          }))
        }
      })
    );

    filteredPrices.forEach((item) => {
      if (item.sku) setTipoCompraForSku(item.sku, 'planilha');
    });
    setLastTipoCompra('planilha');
  };

  const createTagsForProductsNotFound = (
    notFoundItems: ILongSteelOrderItem[]
  ) => {
    const notFoundIndex: number[] = [];
    const taggingObject = notFoundItems.reduce<{ [key: string]: string }>(
      (acc, item, index) => {
        acc[`codigo_material${index + 1}`] = item.sku;
        notFoundIndex.push(index + 1);
        return acc;
      },
      {}
    );
    const noProductsFound = notFoundItems.length === spreadSheetData.length;
    const eventName = `${
      noProductsFound ? 'produto_nao_encontrado' : 'produtos_nao_identificados'
    }`;
    const eventMessage = `Produtos das linhas ${formatIndexes(
      notFoundIndex
    )} não ${noProductsFound ? 'encontrados' : 'identificados'}`;

    dispatch(
      sendTagging({
        etapa: 'enviar_planilha',
        screenName: 'enviar_planilha',
        event: eventName,
        event_category: 'erro',
        ...taggingObject,
        message: eventMessage
      })
    );
  };

  const formatIndexes = (notFoundIndex: number[]) => {
    return notFoundIndex.length > 1
      ? `${notFoundIndex.slice(0, -1).join(', ')} e ${notFoundIndex.slice(-1)}`
      : notFoundIndex[0];
  };

  const spreadSheetSkuData = async (spreadSheetSkuList: string[]) => {
    if (userOrderEntryPreference.codeType === ECodeType.CLIENT_SKU) {
      const results = await getProductForCustomerInternalCode(
        selectedCustomer?.customerId ?? '',
        spreadSheetSkuList,
        ECodeType.CLIENT_SKU,
        selectedCustomer?.division ?? [],
        []
      );

      const internalCodeMap = results.hits.hits.reduce(
        (acc: { [key: string]: string }, hit: Hit) => {
          const product = hit._source;
          acc[product.internal_code] = product.sku;
          return acc;
        },
        {}
      );

      setInternalCodeSkus(internalCodeMap);

      const internalCodeToGerdauSku = spreadSheetSkuList.map(
        (code) => internalCodeMap[code] || code
      );

      const productsPrice: MultProductsResponse[] = await getMultProducts(
        internalCodeToGerdauSku,
        ECodeType.SKU,
        emitterId,
        internalCodeToGerdauSku.length,
        1,
        customerData.division,
        true
      );

      return productsPrice;
    }

    const productsPrice: MultProductsResponse[] = await getMultProducts(
      spreadSheetSkuList,
      ECodeType.SKU,
      emitterId,
      spreadSheetSkuList.length,
      1,
      customerData.division,
      true
    );

    return productsPrice;
  };

  const spreadSheetCatalogBp = async (spreadSheetSkuList: string[]) => {
    const params = magentoURLBuilder({
      term: spreadSheetSkuList.join(','),
      internal_code: userOrderEntryPreference.codeType === ECodeType.CLIENT_SKU,
      pageSize: 30,
      typeSearch: 'list'
    });

    const response = await commerceApi.get(
      `${EndpointsMvp.CATALOG(emitterId, receiverId)}`,
      {
        params,
        headers: getHeaders({ withCommerceToken: true })
      }
    );

    const data = response.data.items.map((item: NewCartItem) => ({
      ...item,
      price: item?.extension_attributes?.price_gerdau
        ? JSON.parse(item?.extension_attributes?.price_gerdau)
        : undefined
    }));

    return data;
  };

  const processPricesAndSendTag = async () => {
    try {
      setLoading(true);
      const filterOnlyFoundProducts = ({ found }: ILongSteelOrderItem) => found;
      const spreadSheetSkuList = spreadSheetData
        .filter(filterOnlyFoundProducts)
        .map((i) => i.sku);

      const productsPrice: MultProductsResponse[] = newSearchItemsEndpointFlag
        ? await spreadSheetCatalogBp(spreadSheetSkuList)
        : await spreadSheetSkuData(spreadSheetSkuList);

      if (productsPrice) {
        setMagentoProducts(productsPrice);

        const filterOnlyFoundProducts = ({ found }: ILongSteelOrderItem) =>
          found;

        const filteredPrices = spreadSheetData
          .filter(filterOnlyFoundProducts)
          .map((i) => {
            const magProduct = productsPrice?.find((mp) => mp.sku === i.sku);
            const price =
              magProduct?.price?.isAgreementPrice === 'X'
                ? String(magProduct?.price?.salesAgreementData?.salesAgreement)
                : magProduct?.price?.creditPrice;
            const gpmDescription = magProduct?.custom_attributes.find(
              (v) => v.attribute_code === 'descricao_gpm'
            );
            const productOptions = magProduct?.productOptions;

            const currentDestination = magProduct?.price?.destination;

            const hasMeasureUnitOptions =
              !!productOptions &&
              !!currentDestination &&
              !!productOptions[currentDestination] &&
              !!productOptions[currentDestination].measureUnits &&
              productOptions[currentDestination].measureUnits.length > 0;

            if (hasMeasureUnitOptions && productOptions && currentDestination) {
              // const measureUnitAvailabel =
              //   productOptions[currentDestination].measureUnits;

              // const hasMeasureUnit = measureUnitAvailabel.some(
              //   (measure: MeasureUnit) => measure.measure === i.measureUnit
              // );

              return {
                ...i,
                code: String(magProduct?.id),
                description: gpmDescription?.value,
                // measureUnit: hasMeasureUnit
                //   ? i.measureUnit
                //   : magProduct?.price?.um,
                measureUnit: i.measureUnit ?? '',
                price,
                hasAgreementPrice:
                  magProduct &&
                  magProduct?.price &&
                  !!magProduct?.price?.isAgreementPrice
              };
            }
            // O magento não da os tipos de unidade de medida disponível. É preciso devolver a unidade de medida válida que vem na resposta do magento. caso contrário quebra o cart
            return {
              ...i,
              code: String(magProduct?.id),
              description: gpmDescription?.value,
              // measureUnit: magProduct?.price?.um,
              measureUnit: i.measureUnit ?? '',
              price,
              hasAgreementPrice:
                magProduct &&
                magProduct?.price &&
                !!magProduct?.price?.isAgreementPrice
            };
          });

        dispatch(
          sendTagging({
            event: 'view_item_list', // alterar para apenas visão do usuário
            ecommerce: {
              screenName: 'sua_planilha',
              tipo_compra: 'planilha',
              paginacao: '',
              items: filteredPrices.map(
                (item) =>
                  ({
                    screenName: 'sua_planilha',
                    tipo_compra: 'planilha',
                    item_id: item.sku ?? '',
                    item_name: item.description,
                    price: item.price,
                    unidade_de_medida: item.measureUnit,
                    quantity: item.quantity?.toString() ?? 1,
                    item_category: item.description ?? '',
                    cod_gerdau: item.sku,
                    preco_acordado: filteredPrices.find(
                      (filteredProduct) => filteredProduct.sku === item.sku
                    )?.hasAgreementPrice
                  } as IProductCardTagging)
              )
            }
          })
        );
        return;
      }

      throw new Error();
    } catch (error) {
      console.error(error);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar informações do(s) item(s)',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const filterPricesAndsearchedItems = () => {
    const filterOnlyFoundProducts = ({ found }: ILongSteelOrderItem) => found;
    return spreadSheetData.filter(filterOnlyFoundProducts).map((i) => {
      const magProduct = magentoProducts?.find((mp) => mp.sku === i.sku);
      const price =
        magProduct?.price?.isAgreementPrice === 'X'
          ? String(magProduct?.price?.salesAgreementData?.salesAgreement)
          : magProduct?.price?.creditPrice;
      const gpmDescription = magProduct?.custom_attributes.find(
        (v) => v.attribute_code === 'descricao_gpm'
      );
      return {
        ...i,
        code: String(magProduct?.id),
        description: gpmDescription?.value,
        price,
        hasAgreementPrice:
          magProduct && magProduct.price && !!magProduct.price.isAgreementPrice
      };
    });
  };

  //get cart
  useEffect(() => {
    cart.getCartIdOrGenerateOne().catch(console.error);
  }, []);

  // precisa melhorar esse tageamento de falha de itens nao encontrados TAGUEAMENTO NAO UTILIZADO
  useEffect(() => {
    const notFoundItems = spreadSheetData.filter((item) => !item.found);
    const itemsNotSentFromLocalStorage = localStorage.getItem('itemsNotSent');
    if (notFoundItems && notFoundItems.length) {
      createTagsForProductsNotFound(notFoundItems);
    }
    if (
      Array.isArray(itemsNotSentFromLocalStorage) &&
      itemsNotSentFromLocalStorage?.length
    ) {
      dispatch(
        sendTagging({
          event: 'limite_produtos_excedido',
          screenName: 'enviar_planilha',
          etapa: 'planilha_carregada'
        })
      );
    }
  }, [spreadSheetData]);

  useEffect(() => {
    if (!customerLoading && spreadSheetData.length && !magentoProducts) {
      processPricesAndSendTag();
    }
  }, [spreadSheetData]);

  useEffect(() => {
    setIsContinueDisabled(
      spreadSheetData.length === 0 ||
        spreadSheetData.every((i) => i.found === false)
    );
  }, [spreadSheetData]);

  const uploadDisabled =
    !emitterId ||
    orderItemSearch?.searchingItems ||
    !!customersState?.loadingBlocks ||
    !!customersState?.loadingProductDescription;

  const canShowSearchedItems = (): boolean => {
    return (
      (pathname.includes(EOrderSettingsRoutes.SEARCH_ITEMS) ||
        pathname.includes(EOrderSettingsRoutes.SPREADSHEET_UPLOAD)) &&
      spreadSheetData.length > 0
    );
  };

  if (!customerData || isBlockedCustomer) {
    return (
      <MessageChooseItem>
        {openBlockedModal && (
          <BlockedModal onCloseModal={onCloseBlockedModal} />
        )}
        <MessageWrapperChooseItem>
          <Message>Selecione emissor e recebedor</Message>
          <Message> para continuar.</Message>
        </MessageWrapperChooseItem>
      </MessageChooseItem>
    );
  }

  return (
    <div style={{ paddingBottom: '6rem' }}>
      <LoadingItems
        enabled={isLoading || orderItemSearch?.searchingItems}
        filename={filename}
        text={isLoading ? 'Processando os itens...' : undefined}
      />
      <ModalRemoveAllSearchedItems />
      {/* <pre>{JSON.stringify(orderItemSearch.searchedItems, null, 2)}</pre> */}
      <Container>
        <Header>
          <Title level={2}>Envie sua planilha</Title>
          <span>
            Crie sua planilha contendo os dados dos produtos e envie para montar
            o seu carrinho através dela.
          </span>
        </Header>
        <ContentButton>
          <a href="/examples/Planilha_padrao_de_pedido.xlsx">
            <Button
              name="templateSpreadsheetModalButton"
              variant="secondary"
              onClick={() => {
                createSpreadsheetSubmissionTags('baixar_modelo_planilha');
              }}
              loading={isLoading}
              disabled={isLoading}
            >
              Baixar modelo de planilha
            </Button>
          </a>
          <Button
            name="configModalButton"
            variant="secondary"
            onClick={() => {
              setCanShowConfigModal(true);
            }}
            loading={isLoading}
            disabled={isLoading}
          >
            Configurar envio da planilha
          </Button>
        </ContentButton>
        {spreadSheetData.length <= 0 && (
          <Wrapper>
            <UploadFile
              acceptedFiles={{
                'application/xls': ['.xls'],
                'application/xlsx': ['.xlsx']
              }}
              maxSizeMB={5}
              buttonLabel="Enviar planilha"
              onChangeFiles={(files) => {
                onFileUploaded(files[0] as RcFile);
                createSpreadsheetSubmissionTags('upload_planilha');
              }}
              disabled={uploadDisabled}
              hideChips
            />
          </Wrapper>
        )}
      </Container>
      {canShowSearchedItems() && (
        <SelfSearchedListMvp
          internalCodeSkus={internalCodeSkus}
          searchedItems={spreadSheetData}
          excededLimit={spreadSheetData.length > 30}
          foundItens={spreadSheetData.filter((item) => item.found).length}
          notFoundItens={spreadSheetData.filter((item) => !item.found).length}
          onRemoveAllItems={removeAllSearchedItem}
          onRemoveSearchedItem={removeSearchedItem}
          onAddSearchedItem={(items: ILongSteelOrderItem[]) =>
            addSearchedItem(items)
          }
          userOrderEntryPreference={userOrderEntryPreference}
          magentoProducts={magentoProducts}
          loading={isLoading}
          cleanMagentoProducts={cleanMagentoProducts}
          onFileUploaded={onFileUploaded}
        />
      )}

      <ModalProductNotFound />

      <SpreadsheetConfigModal
        canShowModal={canShowConfigModal && !isLoading}
        setCanShowModal={setCanShowConfigModal}
        orderEntryPreference={userOrderEntryPreference}
        onConfirmClick={onConfirmSpreadsheetConfig}
      />

      {!canShowConfigModal && (
        <ShoppingCartBottomBar
          isContinueDisabled={isContinueDisabled}
          onGoToCheckout={() =>
            goToNextStep(
              spreadSheetData.filter((item) => !item.found).length > 0
            )
          }
          showQuantity={false}
          loading={isLoading}
        />
      )}
    </div>
  );
};
